import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Props for the FilePreviewModal component.
 */
interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  type: 'image' | 'video';
  name: string;
}

/**
 * Modal overlay for previewing image and video files.
 * Supports keyboard Escape to close, backdrop click to close, and focus trapping.
 */
const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  url,
  type,
  name,
}) => {
  const triggerRef = useRef<Element | null>(null);

  // Store the element that opened the modal so we can return focus to it on close
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
    } else if (triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white rounded-lg shadow-xl p-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Preview: ${name}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-gray-700 truncate max-w-[calc(100%-3rem)]">
                {name}
              </h2>
              <button
                autoFocus
                onClick={onClose}
                aria-label="Close preview"
                className="ml-2 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Media content */}
            <div className="flex items-center justify-center">
              {type === 'video' ? (
                <video
                  src={url}
                  controls
                  autoPlay
                  className="max-h-[70vh] w-full rounded-lg"
                />
              ) : (
                <img
                  src={url}
                  alt={name}
                  className="max-h-[70vh] w-auto rounded-lg object-contain"
                />
              )}
            </div>
          </motion.div>
        </div>
      , document.body)}
    </AnimatePresence>
  );
};

export default FilePreviewModal;
